import { ORDER_TYPES } from './orderType';

/**
 * What "Most Popular" means, per order type.
 *
 * ⚠️ IT IS A CURATED ORDER, NOT A SALES FIGURE. The option was labelled
 * "Recommended" until 2026-09-28, when Alex renamed it; nothing about how it is
 * computed changed with the label. Until the lists below are filled in it is
 * category order, and even then it is an editor's opinion. If it should ever be
 * driven by actual sales, that is a different source (the popularity report) and
 * a deliberate change — not something to quietly infer from the name.
 *
 * The shelf's default order used to be the pricelist's own `position` — one
 * merchandising decision for every customer. But the right thing to lead with
 * depends on why they are here: a senior came for announcements, a picture-day
 * parent came for extra prints, a sports parent for magnets.
 *
 * TWO LEVELS, and `productOrder` wins:
 *
 *   productOrder   exact products, most-recommended first, by `css_name` —
 *                  the value /admin/addons sets, NOT the display name, because
 *                  names carry quantities and prices ("(8) Wallets") and change
 *                  season to season.
 *   categoryOrder  the fallback for everything productOrder does not name, so a
 *                  new product still lands somewhere sensible instead of at the
 *                  end.
 *
 * Anything named in neither keeps its arriving pricelist order, behind
 * everything that is named. Sorting is stable, so equal-ranked products stay in
 * the order the server sent them.
 */

// The vocabulary the lists below draw from: Addon::GIFT_CATEGORIES —
// announcements, magnets, gifts ("Photo Gifts"), prints ("Extra Prints"),
// digital ("Digital Files"). Exported so a test can prove no config names a
// category that does not exist.
export const ALL_CATEGORIES = ['announcements', 'magnets', 'gifts', 'prints', 'digital'];

const POPULARITY_ORDER = {
  // Leads with announcements, then photo gifts. Alex, 2026-09-29.
  [ORDER_TYPES.SENIOR]: {
    // No product-level opinion yet: categoryOrder decides, and within a category
    // the shelf keeps the pricelist's own `position` from /admin/addons. Fill
    // this with css_names, most-popular first, to overrule that per product.
    productOrder: [],
    categoryOrder: ['announcements', 'gifts', 'magnets', 'prints', 'digital'],
  },

  // Leads with PHOTO GIFTS. Alex, 2026-09-29: "the photo gifts should always be
  // shown first. except for seniors, in which it is announcements - gifts".
  //
  // ⚠️ `announcements` IS DELIBERATELY ABSENT, as specified. A category left out
  // of the list is NOT hidden -- `rankIn` gives it MAX_SAFE_INTEGER, so it sorts
  // LAST. A graduation announcement on a picture-day shelf therefore falls to
  // the end of the grid rather than disappearing from it.
  [ORDER_TYPES.SCHOOL]: {
    productOrder: [],
    categoryOrder: ['gifts', 'magnets', 'prints', 'digital'],
  },

  // Leads with photo gifts. Alex, 2026-09-29.
  // `announcements` deliberately absent -- see the note on SCHOOL above.
  [ORDER_TYPES.SPORTS]: {
    productOrder: [],
    categoryOrder: ['gifts', 'magnets', 'prints', 'digital'],
  },

  // No order type resolved. BOTH LISTS EMPTY, deliberately: with nothing known
  // about why this customer is here, the pricelist's own merchandising is a
  // better answer than a guess at one. Empty lists make the comparator return 0
  // for every pair, and the sort is stable, so the server's order survives
  // exactly as sent.
  [ORDER_TYPES.UNKNOWN]: {
    productOrder: [],
    categoryOrder: [],
  },
};

export default POPULARITY_ORDER;

const rankIn = (list, value) => {
  const i = list.indexOf(value);

  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

/**
 * Compare two add-ons for "Most Popular", given an order type.
 *
 * Returns 0 for products this config has no opinion about, which — because the
 * sort is stable — leaves them in the order the server sent.
 */
export function popularityComparator(orderType) {
  const { productOrder, categoryOrder } = POPULARITY_ORDER[orderType] || POPULARITY_ORDER[ORDER_TYPES.UNKNOWN];

  return (a, b) => {
    const byProduct = rankIn(productOrder, a.cssName) - rankIn(productOrder, b.cssName);
    if (byProduct !== 0) return byProduct;

    return rankIn(categoryOrder, a.giftCategory) - rankIn(categoryOrder, b.giftCategory);
  };
}
